import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMedication } from '@/shared/types/interfaces/medication';
import { Alert, Button, Input } from '@/shared/ui';
import { makeRequest } from '@/shared/services/http';
import { ABSOLUTE_ROUTES } from '../../../../../../../shared/constants/routes';
import { isNumberZeroOrGreater } from '@/shared/helpers/validations';
import { PackagingEnum } from '../../../../../../../shared/types/enums/packaging';
import { FormLabel } from '../../formLabel';

interface IProps {
    medication: IMedication;
    onCreateMedication?: (quantity: string, packaging: PackagingEnum) => void;
    otc?: boolean;
    loading?: boolean;
    isSubmittingEditQuantityOnly?: boolean;
    formik: any;
    hideButtons?: boolean;
    isRx?: boolean;
    goBack?: () => void;
    isMobile?: boolean;
}

export const UnitMedicationForm = ({ formik, medication, otc = false, hideButtons = false, isRx = false, goBack, isMobile, loading = false }: IProps) => {
    const navigate = useNavigate();
    const [otcPriceLoading, setOtcPriceLoading] = useState({ loading: false, value: null });

    const fetchOtcPrice = async (quantity: string, medId: string, signal: AbortSignal) => {
        const body = new URLSearchParams();
        body.append('quantity', quantity); // e.g., '90'

        const price = await makeRequest<IMedication[]>('post', 'prescribables', `${medId}/estimated-price`, body.toString(), {
            signal,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
        return price;
    };

    const fetchOtcCost = async (signal: AbortSignal) => {
        if (formik.values.quantity) {
            setOtcPriceLoading({ loading: true, value: null });
            const parsedQuantity = parseInt(formik.values.quantity, 10);
            const params = {
                medid: medication.medid.toString(),
                quantity: parsedQuantity
            };
            try {
                const res = await fetchOtcPrice(params.quantity.toString(), params.medid, signal);
                setOtcPriceLoading({ loading: false, value: res });
            } catch (e) {
                setOtcPriceLoading({ loading: false, value: null });
                console.error('Failed to fetch OTC cost', e);
            }
        }
    };

    const handleGoBack = () => {
        if (goBack) {
            goBack();
        } else {
            navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION);
        }
    };

    useEffect(() => {
        if (!otc) return;
        if (!formik.values.quantity) return; // optionally skip empty quantity
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            fetchOtcCost(controller.signal);
        }, 1000);

        return () => {
            setOtcPriceLoading({ loading: false, value: null });
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [formik.values.quantity]);

    const continueButtonText = useMemo(() => {
        return otc ? 'Save & Add' : 'Save & Continue';
    }, [otc]);


    return (
        <div className="w-full">
            <div
                data-testid="quantity-form"
                className={"flex flex-col gap-[20px]"}
            >
                <div className={`rounded-xl bg-[#CCE3DA99] px-[15px] py-[20px] sm:p-[30px] ${formik.errors.quantity ? 'border-2 border-red-500' : ''}`}>
                    <FormLabel
                        className="mb-[.9375rem] sm:mb-[1.25rem]"
                        label={
                            isRx
                                ? 'See what quantity (QTY) is listed on your existing prescription label'
                                : 'What quantity or volume number listed on this item? (For example: 30 g or 120 mL)'
                        }
                    />
                    <div className="flex flex-wrap items-start gap-[0.625rem]">
                        <div className="flex-1 min-w-[140px]">
                            <Input
                                label="Quantity (QTY)"
                                name="quantity"
                                type="text"
                                required
                                placeholder="ex. 30"
                                value={formik.values.quantity}
                                onChange={e => {
                                    const rawValue = e.target.value;
                                    let sanitizedValue = rawValue.replace(/[^0-9]/g, '');
                                    sanitizedValue = sanitizedValue.replace(/^0+/, '');
                                    if (!sanitizedValue || sanitizedValue === '0') {
                                        formik.setFieldValue('quantity', '');
                                    } else {
                                        formik.setFieldValue('quantity', sanitizedValue);
                                    }
                                    formik.setErrors({});
                                }}
                                onBlur={formik.handleBlur}
                                error={!!formik.errors.quantity}
                                labelClassName={`${formik.errors.quantity ? 'text-red-500' : ''} `}
                                className="w-full"
                                enableNaturalEnter
                            />
                        </div>
                    </div>
                    {formik.errors.quantity && formik.touched.quantity && <Alert boldLabel={false} className='mt-[0.625rem]' label={formik.errors.quantity} />}
                </div>
                {isNumberZeroOrGreater(otcPriceLoading.value === null ? undefined : otcPriceLoading.value) && <div className={`relative bg-mint-200 flex flex-col rounded-[0.8125rem] p-5 md:p-7.5`}>
                    <div className='flex flex-col min-h-[48px]'>
                        <div className='flex flex-col gap-[0.625rem]'>
                            <div className='flex flex-row justify-between'>
                                <p className="font-bold font-atkinsonBold text-20">Estimated price</p>
                                <p className="font-bold font-atkinsonBold text-20">
                                    {`${isNumberZeroOrGreater(otcPriceLoading.value === null ? undefined : otcPriceLoading.value) && formik.values.quantity ? `$${otcPriceLoading.value?.toFixed(2)}` : '_'}`}
                                </p>
                            </div>
                            <div className='text-16 font-dmSans text-primary-900'>
                                This cost reflects the quantity entered. You can edit the amount above if needed.
                            </div>
                        </div>
                    </div>
                </div>}
                {!hideButtons && <Button
                    className="prescriptions__otc_form_save_btn"
                    type="submit"
                    showEndIcon
                    disabled={loading}
                    testId="otcForm-save-btn"
                    fontClassName="font-bold"
                    onClick={(e) => {
                        e.preventDefault();
                        formik.handleSubmit();
                    }}
                >
                    {continueButtonText}
                </Button>}
            </div>
            {!hideButtons && !isMobile && <Button
                variant="outlined"
                className="prescriptions__add-medication__back mt-[15px] w-full"
                disabled={loading}
                onClick={handleGoBack}
                testId="button-medication-add-back"
                fontClassName='font-bold'
            >
                Back
            </Button>}
        </div>
    );
};
